// Écarts (avec leurs preuves côte à côte), questions de clarification, exclusions proposées, postes à ajouter au chiffrage,
// et courriers de questions et de réserves (préparation, liste, envoi).
import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Mail, SearchCheck } from 'lucide-react';
import { api, messageErreur } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import { date, pluriel } from '../../lib/format.ts';
import { Alerte, Badge, Bouton, Chargement, ChampChoix, ChampTexte, Dialogue, EtatErreur, EtatVide, LienBouton, Onglets, Panneau, useNotifier } from '../../components/ui.tsx';
import { BadgeStatutExigence } from '../../components/statuts.tsx';
import { DossierCadre } from './DossierCadre.tsx';
import {
  BadgePriorite, CONTROLES_CHIFFRAGE, DESTINATAIRES, Extrait, LienSource, STATUTS_ECART, STATUTS_QUESTION, TYPES_ECART, TYPES_PIECE, options,
  type Ecart, type Exigence, type Question,
} from './commun.tsx';
import { MODELES, MOYENS, QUALITES, type CourrierListe } from './PageCourrier.tsx';

interface ReponseEcarts {
  ecarts: Ecart[];
  exclusions: { code: string | null; libelle: string; exclusion: string; exigenceId: string | null; statut: string | null; origine: string }[];
  postesAAjouter: { id: string; label: string; domaine: string | null; detail: string | null }[];
}
type Onglet = 'ecarts' | 'questions' | 'chiffrage' | 'courriers';

export function PageEcarts() {
  const { id = '' } = useParams();
  const { peut } = useSession();
  const naviguer = useNavigate();
  const notifier = useNotifier();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [preparation, setPreparation] = useState(false);
  const [choixModele, setChoixModele] = useState(false);
  const onglet = (['ecarts', 'questions', 'chiffrage', 'courriers'].includes(params.get('onglet') ?? '') ? params.get('onglet') : 'ecarts') as Onglet;
  const q = useQuery({ queryKey: ['etudes-ecarts', id], queryFn: () => api.get<ReponseEcarts>(`/api/dossiers/${id}/ecarts`) });
  const qs = useQuery({ queryKey: ['etudes-questions', id], queryFn: () => api.get<Question[]>(`/api/dossiers/${id}/questions`) });
  const qc2 = useQuery({ queryKey: ['etudes-courriers', id], queryFn: () => api.get<CourrierListe[]>(`/api/dossiers/${id}/courriers`) });
  const qp = useQuery({ queryKey: ['etudes-pieces', id], queryFn: () => api.get<{ pieces: { doc_type: string }[] }>(`/api/dossiers/${id}/pieces`) });
  const ouverts = q.data?.ecarts.filter((g) => g.status === 'ouvert' && !g.informatif).length;
  const aPoser = qs.data?.filter((x) => x.status === 'a_poser').length;
  // Modèle proposé : « avis sur devis reçu » quand le dossier contient le devis d'une entreprise et aucune DPGF ni bordereau à chiffrer.
  const types = new Set((qp.data?.pieces ?? []).map((p) => p.doc_type));
  const modeleSuggere = (types.has('devis_existant') || types.has('avenant')) && !types.has('dpgf') && !types.has('bordereau') ? 'avis_devis' : 'questions_offre';
  // « Préparer le courrier » reprend le brouillon en cours s'il existe (un seul courrier à la fois en préparation), sinon choix du modèle.
  const preparer = () => {
    const brouillon = (qc2.data ?? []).find((c) => c.status === 'brouillon');
    if (brouillon) { naviguer(`/etudes/${id}/courriers/${brouillon.id}`); return; }
    setChoixModele(true);
  };
  const creer = async (modele: string) => {
    setPreparation(true);
    try {
      const cible = (await api.post<{ id: string }>(`/api/dossiers/${id}/courriers`, { modele })).id;
      qc.invalidateQueries({ queryKey: ['etudes-courriers', id] });
      naviguer(`/etudes/${id}/courriers/${cible}`);
    } catch (e) { notifier(messageErreur(e), true); setPreparation(false); }
  };
  return (
    <DossierCadre dossierId={id} actif="ecarts" large actions={<>
      {peut('courrier.ecrire') && <Bouton variante="principal" icone={<Mail aria-hidden />} chargement={preparation} disabled={qc2.isLoading} onClick={preparer}>Préparer le courrier</Bouton>}
      <a className="btn" href={`/api/dossiers/${id}/export/questions.xlsx`} download><Download aria-hidden />Exporter les questions (XLSX)</a>
    </>}>
      <Onglets libelle="Écarts, questions, chiffrage et courriers" actif={onglet} changer={(o) => { const p = new URLSearchParams(params); p.set('onglet', o); setParams(p, { replace: true }); }}
        onglets={[{ id: 'ecarts', libelle: 'Écarts', compteur: ouverts }, { id: 'questions', libelle: 'Questions', compteur: aPoser },
          { id: 'chiffrage', libelle: 'Exclusions et postes à chiffrer', compteur: q.data ? q.data.exclusions.length + q.data.postesAAjouter.length : undefined },
          { id: 'courriers', libelle: 'Courriers', compteur: qc2.data?.length }]} />
      {(q.isLoading || qs.isLoading) && <Chargement lignes={5} />}
      {q.isError && <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />}
      {q.data && onglet === 'ecarts' && <ListeEcarts dossierId={id} ecarts={q.data.ecarts} />}
      {qs.data && onglet === 'questions' && <ListeQuestions dossierId={id} questions={qs.data} />}
      {q.data && onglet === 'chiffrage' && <Chiffrage dossierId={id} data={q.data} />}
      {onglet === 'courriers' && (qc2.isError ? <EtatErreur erreur={qc2.error} reessayer={() => qc2.refetch()} />
        : qc2.data && <ListeCourriers dossierId={id} courriers={qc2.data} />)}
      {choixModele && <ChoixModele suggere={modeleSuggere} chargement={preparation} fermer={() => setChoixModele(false)} valider={creer} />}
    </DossierCadre>
  );
}

/** Choix du modèle à la création du courrier : nos questions avant offre, ou avis sur le devis d'un tiers. */
function ChoixModele({ suggere, chargement, fermer, valider }: { suggere: string; chargement: boolean; fermer: () => void; valider: (m: string) => void }) {
  const [modele, setModele] = useState(suggere);
  return (
    <Dialogue titre="Préparer un courrier" ouvert fermer={fermer}
      pied={<><Bouton onClick={fermer}>Annuler</Bouton><Bouton variante="principal" chargement={chargement} onClick={() => valider(modele)}>Préparer le courrier</Bouton></>}>
      <fieldset className="pile co-modeles">
        <legend className="petit secondaire">Quel courrier préparer ? Les points sont repris du dossier ; tout reste modifiable avant l’émission.</legend>
        {Object.entries(MODELES).map(([cle, m]) => (
          <label key={cle} className={`case co-modele ${modele === cle ? 'co-modele-actif' : ''}`}>
            <input type="radio" name="modele-courrier" value={cle} checked={modele === cle} onChange={() => setModele(cle)} />
            <span className="pile" style={{ gap: 2 }}>
              <strong>{m.libelle}{cle === suggere && <> <Badge ton="info" icone={false}>Proposé pour ce dossier</Badge></>}</strong>
              <span className="petit secondaire">{m.aide}</span>
            </span>
          </label>
        ))}
      </fieldset>
    </Dialogue>
  );
}

function ListeCourriers({ dossierId, courriers }: { dossierId: string; courriers: CourrierListe[] }) {
  if (!courriers.length) return (
    <Panneau><EtatVide icone={<Mail />} titre="Aucun courrier"
      texte="« Préparer le courrier » reprend les questions, contradictions, ambiguïtés, contrôles du chiffrage, exclusions et pièces manquantes du dossier : questions avant de remettre votre offre, ou avis sur le devis d’une entreprise adressé au client." /></Panneau>
  );
  return (
    <Panneau serre titre={`Courriers du dossier (${courriers.length})`}>
      <ul className="co-liste">
        {courriers.map((c) => (
          <li key={c.id} className="co-ligne">
            <div className="co-ligne-corps">
              <span className="co-ligne-titre">{c.numero ? `Courrier ${c.numero}` : 'Courrier en préparation'}{c.version_no > 1 ? ` — version ${c.version_no}` : ''}</span>
              <span className="co-ligne-meta petit secondaire">
                <span>{MODELES[c.modele ?? 'questions_offre']?.libelle}</span>
                <span>Daté du {date(c.date_courrier)}</span>
                <span>{c.dest_nom || c.dest_societe ? `À : ${[c.dest_nom, c.dest_societe].filter(Boolean).join(', ')}${c.dest_qualite ? ` (${QUALITES[c.dest_qualite] ?? c.dest_qualite})` : ''}` : 'Destinataire à préciser'}</span>
                <span>{pluriel(c.nb_points, 'point')}</span>
                {c.precedent_numero && <span>Remplace {c.precedent_numero}</span>}
              </span>
            </div>
            <span className="rangee" style={{ gap: 6 }}>
              {c.status === 'emis' ? <Badge ton="succes">Émis</Badge> : <Badge ton="ambre">Brouillon</Badge>}
              {c.status === 'emis' && (c.envoye_le
                ? <Badge ton="neutre" icone={false}>Envoyé le {date(c.envoye_le)}{c.envoye_moyen ? ` — ${MOYENS[c.envoye_moyen] ?? c.envoye_moyen}` : ''}</Badge>
                : <Badge ton="ambre">Envoi non renseigné</Badge>)}
            </span>
            <LienBouton compact to={`/etudes/${dossierId}/courriers/${c.id}`}>{c.status === 'brouillon' ? 'Reprendre' : 'Ouvrir'}</LienBouton>
          </li>
        ))}
      </ul>
    </Panneau>
  );
}

function ListeEcarts({ dossierId, ecarts }: { dossierId: string; ecarts: Ecart[] }) {
  const { peut } = useSession();
  const exigences = useQuery({ queryKey: ['etudes-exigences', dossierId], queryFn: () => api.get<Exigence[]>(`/api/dossiers/${dossierId}/exigences`) });
  const libelles = new Map((exigences.data ?? []).map((e) => [e.id, e.label]));
  const qc = useQueryClient();
  const notifier = useNotifier();
  if (!ecarts.length) return <Panneau><EtatVide icone={<SearchCheck />} titre="Aucun écart relevé" texte="Les écarts apparaissent après l’analyse des pièces." /></Panneau>;
  const changer = async (g: Ecart, status: string) => {
    try {
      await api.patch(`/api/ecarts/${g.id}`, { status });
      await qc.invalidateQueries({ queryKey: ['etudes-ecarts', dossierId] });
      qc.invalidateQueries({ queryKey: ['dossier', dossierId] });
      qc.invalidateQueries({ queryKey: ['etudes-synthese', dossierId] });
    } catch (e) { notifier(messageErreur(e), true); }
  };
  return (
    <div className="pile-l">
      {Object.entries(TYPES_ECART).map(([kind, t]) => {
        const groupe = ecarts.filter((g) => g.kind === kind);
        if (!groupe.length) return null;
        return (
          <section key={kind} className="pile" aria-labelledby={`et-groupe-${kind}`}>
            <h2 id={`et-groupe-${kind}`} className="et-groupe-titre"><Badge ton={t.ton}>{t.pluriel}</Badge><span className="petit discret">{pluriel(groupe.filter((g) => g.status === 'ouvert' && !g.informatif).length, 'ouvert')} sur {groupe.filter((g) => !g.informatif).length}{groupe.some((g) => g.informatif) ? ` — ${pluriel(groupe.filter((g) => g.informatif).length, 'constat informatif', 'constats informatifs')}` : ''}</span></h2>
            {kind === 'contradiction' && <p className="petit secondaire">L’outil ne décide jamais quelle pièce prime : l’ordre de priorité contractuelle des pièces est à demander au maître d’œuvre.</p>}
            {kind === 'controle_chiffrage' && <p className="petit secondaire">Vérifications de la pièce de chiffrage, ligne par ligne : validité, doublons possibles, quantités, puissances et références, calculs au centime. Un doublon ou une incohérence est toujours « à confirmer » par l’entreprise.</p>}
            {groupe.map((g) => (
              <article key={g.id} className={`panneau et-ecart ${g.status !== 'ouvert' ? 'et-ecart-clos' : ''}`}>
                <div className="et-ecart-entete">
                  <div className="pile" style={{ gap: 4, minWidth: 0, flex: 1 }}>
                    <span className="rangee" style={{ gap: 6, flexWrap: 'wrap' }}>
                      {g.status === 'ouvert' && <BadgePriorite priorite={g.priorite} motif={g.priorite_motif} />}
                      {g.controle && <Badge ton={g.informatif ? 'neutre' : 'ambre'} icone={false}>{CONTROLES_CHIFFRAGE[g.controle] ?? g.controle}</Badge>}
                      {g.informatif && <Badge ton="info" icone={false}>Information</Badge>}
                    </span>
                    <h3 className="et-ecart-titre">{g.title}</h3>
                    {g.description && <p className="petit secondaire">{g.description}</p>}
                  </div>
                  <label className="champ et-ecart-statut"><span className="champ-libelle">Statut</span>
                    <select className="saisie" value={g.status} disabled={!peut('dossier.ecrire')} onChange={(e) => changer(g, e.target.value)}>
                      {options(STATUTS_ECART).map((o) => <option key={o.valeur} value={o.valeur}>{o.libelle}</option>)}
                    </select></label>
                </div>
                {g.sources.length > 0 && (
                  <div className={`et-preuves ${g.kind === 'contradiction' ? 'et-preuves-cote' : ''}`}>
                    {g.sources.map((s, i) => (
                      <div key={i} className="et-preuve">
                        <span className="tres-petit discret">{TYPES_PIECE[s.doc_type] ?? s.doc_type}{s.version_label ? ` — version ${s.version_label}` : ''}</span>
                        <Extrait texte={s.excerpt} />
                        <LienSource versionId={s.version_id} page={s.page} chunkId={s.chunk_id} titre={s.titre} localisation={s.locator} compact />
                      </div>
                    ))}
                  </div>
                )}
                {g.requirement_ids.length > 0 && (
                  <div className="groupe-btn">
                    <span className="tres-petit discret">Exigences liées :</span>
                    {g.requirement_ids.slice(0, 5).map((rid) => <LienBouton key={rid} compact variante="fantome" to={`/etudes/${dossierId}/exigences?e=${rid}`}>{libelles.get(rid) ?? 'Exigence'}</LienBouton>)}
                  </div>
                )}
              </article>
            ))}
          </section>
        );
      })}
    </div>
  );
}

function ListeQuestions({ dossierId, questions }: { dossierId: string; questions: Question[] }) {
  const { peut } = useSession();
  const ecrire = peut('dossier.ecrire');
  const qc = useQueryClient();
  const notifier = useNotifier();
  const [nouvelle, setNouvelle] = useState({ text: '', addressee: 'maitre_oeuvre' });
  const [filtre, setFiltre] = useState('');
  const rafraichir = () => { qc.invalidateQueries({ queryKey: ['etudes-questions', dossierId] }); qc.invalidateQueries({ queryKey: ['etudes-synthese', dossierId] }); };
  const maj = async (x: Question, corps: Record<string, unknown>) => {
    try { await api.patch(`/api/questions/${x.id}`, corps); rafraichir(); } catch (e) { notifier(messageErreur(e), true); }
  };
  const ajouter = async () => {
    try {
      await api.post(`/api/dossiers/${dossierId}/questions`, { text: nouvelle.text.trim(), addressee: nouvelle.addressee });
      setNouvelle({ ...nouvelle, text: '' }); notifier('Question ajoutée.'); rafraichir();
    } catch (e) { notifier(messageErreur(e), true); }
  };
  const liste = questions.filter((x) => !filtre || x.addressee === filtre);
  return (
    <div className="pile-l">
      <div className="rangee" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <ChampChoix libelle="Destinataire" value={filtre} vide="Tous" onChange={(e) => setFiltre(e.target.value)} options={options(DESTINATAIRES)} />
        <p className="petit secondaire" style={{ flex: 1, minWidth: 220 }}>Questions prêtes à envoyer, avec l’extrait qui les justifie, les plus importantes d’abord (bloquant, important, complément). L’export XLSX les regroupe par destinataire.</p>
      </div>
      {liste.length === 0 && <Panneau><EtatVide titre="Aucune question" texte="Les questions sont générées par l’analyse ; vous pouvez en ajouter." /></Panneau>}
      <ul className="et-questions">
        {liste.map((x) => (
          <li key={x.id} className="panneau et-question">
            <div className="rangee" style={{ gap: 8, flexWrap: 'wrap' }}>
              {x.status === 'a_poser' && <BadgePriorite priorite={x.priorite} motif={x.priorite_motif} />}
              <Badge icone={false}>{DESTINATAIRES[x.addressee ?? ''] ?? 'Destinataire à préciser'}</Badge>
              <Badge ton={x.status === 'a_poser' ? 'ambre' : x.status === 'repondue' ? 'succes' : 'neutre'}>{STATUTS_QUESTION[x.status]}</Badge>
              {x.exigence && <span className="tres-petit discret">Exigence : {x.exigence}</span>}
              {x.ecart && <span className="tres-petit discret">Écart : {TYPES_ECART[x.ecart_type ?? '']?.libelle ?? ''}</span>}
              {x.asked_at && x.courrier_id && (
                <span className="tres-petit">Posée le {date(x.asked_at)} (<Link to={`/etudes/${dossierId}/courriers/${x.courrier_id}`}>courrier {x.courrier_numero}</Link>)</span>)}
            </div>
            <p>{x.text}</p>
            {x.source_excerpt && (
              <div className="pile" style={{ gap: 4 }}>
                <Extrait texte={x.source_excerpt} />
                <LienSource versionId={x.source_version_id} page={x.source_page} titre={x.exigence_document ?? 'Demande du client'} localisation={x.source_locator} compact />
              </div>
            )}
            <div className="et-question-champs">
              <ChampChoix libelle="Statut" value={x.status} disabled={!ecrire} onChange={(e) => maj(x, { status: e.target.value })} options={options(STATUTS_QUESTION)} />
              <ChampChoix libelle="Destinataire" value={x.addressee ?? ''} vide="À préciser" disabled={!ecrire} onChange={(e) => maj(x, { addressee: e.target.value || null })} options={options(DESTINATAIRES)} />
            </div>
            {(x.status === 'repondue' || x.status === 'posee' || x.answer) && (
              <ChampTexte libelle="Réponse reçue" rows={2} defaultValue={x.answer ?? ''} readOnly={!ecrire}
                onBlur={(e) => { if (e.target.value !== (x.answer ?? '')) maj(x, { answer: e.target.value || null, ...(e.target.value && x.status !== 'repondue' ? { status: 'repondue' } : {}) }); }} />
            )}
          </li>
        ))}
      </ul>
      {ecrire && (
        <Panneau titre="Ajouter une question">
          <div className="pile">
            <ChampTexte libelle="Question" rows={3} value={nouvelle.text} onChange={(e) => setNouvelle({ ...nouvelle, text: e.target.value })} />
            <div className="rangee" style={{ gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <ChampChoix libelle="Destinataire" value={nouvelle.addressee} onChange={(e) => setNouvelle({ ...nouvelle, addressee: e.target.value })} options={options(DESTINATAIRES)} />
              <Bouton disabled={nouvelle.text.trim().length < 3} onClick={ajouter}>Ajouter la question</Bouton>
            </div>
          </div>
        </Panneau>
      )}
    </div>
  );
}

function Chiffrage({ dossierId, data }: { dossierId: string; data: ReponseEcarts }) {
  return (
    <div className="pile-l">
      <Panneau titre={`Postes à ajouter au chiffrage (${data.postesAAjouter.length})`} actions={<LienBouton to={`/etudes/${dossierId}/devis`} variante="principal" compact>Ouvrir le devis</LienBouton>}>
        {data.postesAAjouter.length === 0
          ? <p className="secondaire">Aucune exigence qualifiée « Manquant ». Qualifiez les exigences non chiffrées dans l’écran Exigences.</p>
          : <ul className="et-liste-simple">{data.postesAAjouter.map((p) => (
            <li key={p.id}><Link to={`/etudes/${dossierId}/exigences?e=${p.id}`}>{p.label}</Link>{p.domaine && <span className="tres-petit discret"> — {p.domaine}</span>}{p.detail && <span className="petit secondaire"> — {p.detail}</span>}</li>))}</ul>}
      </Panneau>
      <Panneau titre={`Exclusions proposées (${data.exclusions.length})`}>
        {data.exclusions.length === 0 ? <p className="secondaire">Aucune exclusion proposée.</p> : (
          <div className="pile">
            <Alerte ton="ambre">Propositions à confirmer avec le maître d’œuvre avant de les reporter dans le devis : rien n’est exclu d’office.</Alerte>
            <ul className="et-exclusions">
              {data.exclusions.map((x, i) => (
                <li key={i}>
                  <strong className="petit">{x.libelle}</strong>
                  <p>{x.exclusion}</p>
                  <span className="rangee" style={{ gap: 6 }}>
                    {x.statut && <BadgeStatutExigence statut={x.statut} />}
                    {x.exigenceId && <Link className="petit" to={`/etudes/${dossierId}/exigences?e=${x.exigenceId}`}>Voir l’exigence</Link>}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </Panneau>
    </div>
  );
}
